import request from '../Http'
import {HttpMethodEnum} from '../../utils/libs';
import {API_CONFIG} from '../config';

/**
 * @namespace API
 * @description API 方法集合
 */
const API = {
    /**
     * @function providers
     * @description 取得登入GCIP
     * @param {Object} data - 註冊數據
     * @returns {Promise} 註冊請求的 Promise 對象
     */
    providers(data) {
        return request({
            url: '/v1/auth/providers',
            method: HttpMethodEnum.GET,
            data
        });
    },

    /**
     * @function login
     * @description 用戶登入
     * @param {Object} data - 登入數據
     * @returns {Promise} 登入請求的 Promise 對象
     */
    login(data) {
        return request({
            url: '/v1/auth/login',
            method: HttpMethodEnum.POST,
            data
        });
    },
    /**
     * @function refreshToken
     * @description 刷新登入權限token
     * @returns {Promise} 登入請求的 Promise 對象
     */
    refreshToken() {
        return request({
            url: '/v1/auth/refresh',
            method: HttpMethodEnum.POST
        })
    },

    /**
     * @function logout
     * @description 用戶登出
     * @param {Object} data - 登出數據
     * @returns {Promise} 登出請求的 Promise 對象
     */
    logout(data) {
        return request({
            url: '/v1/user/logout',
            method: HttpMethodEnum.POST,
            data
        });
    },

    /**
     * @function getProfileInfo
     * @description 獲取用戶資料
     * @param {Object} params - 查詢參數
     * @returns {Promise} 獲取用戶資料請求的 Promise 對象
     */
    getProfileInfo(params) {
        return request({
            url: '/v1/users/me',
            method: HttpMethodEnum.GET,
            params
        });
    },

    /**
     * @function updateProfile
     * @description 編輯用戶資料
     * @param {Object} data - 查詢參數
     * @returns {Promise} 獲取用戶資料請求的 Promise 對象
     */
    updateProfile(data) {
        return request({
            url: '/v1/users/me',
            method: HttpMethodEnum.PATCH,
            data
        });
    },

    /**
     * @function fetchKnowledgeBase
     * @description 獲取知識庫信息
     * @param {string} orgId - 組織 ID
     * @param {string} projectId - 項目 ID
     * @returns {Promise} 代表知識庫數據的 Promise 對象
     */
    fetchKnowledgeBase(orgId, projectId) {
        return request({
            url: `/v1/organizations/${orgId}/projects/${projectId}/knowbase`,
            method: HttpMethodEnum.GET,
        });
    },

    /**
     * @function getKnowledgeBase
     * @description 獲取知識庫信息
     * @param {string} orgId - 組織 ID
     * @param {string} projectId - 項目 ID
     * @param {string} category - 知識庫類別
     * @param {Object} params - 查詢參數
     * @returns {Promise} 代表知識庫數據的 Promise 對象
     */
    getKnowledgeBase(orgId, projectId, category, params = {}) {
        return request({
            url: `/v1/organizations/${orgId}/projects/${projectId}/knowbase/documents`,
            method: HttpMethodEnum.GET,
            params: {
                category,
                ...params  // 這裡會包含 page 和 limit 參數
            }
        });
    },

    /**
     * @function downloadDocument
     * @description 下載知識庫已上傳的檔案
     * @param {string} orgId - 組織 ID
     * @param {string} projectId - 項目 ID
     * @param {string} knowbaseId
     * @param {string} documentId - 文件 ID
     * @returns {Promise} 代表知識庫數據的 Promise 對象
     */
    downloadDocument(orgId, projectId, knowbaseId, documentId) {
        return request({
            url: `/v1/organizations/${orgId}/projects/${projectId}/knowbase/documents/${documentId}/download`,
            method: HttpMethodEnum.GET,
            responseType: 'blob'  // 重要：需要設置 responseType 為 'blob'
        });
    },

    /**
     * @function uploadDocument
     * @description 知識庫上傳
     * @param {string} orgId - 組織 ID
     * @param {string} projectId - 項目 ID
     * @param {string} knowbaseId - 知識庫 ID
     * @param formData
     * @returns {Promise} 代表知識庫數據的 Promise 對象
     */
    uploadDocument: async (orgId, projectId, knowbaseId, formData) => {
        return request({
            url: `/v1/organizations/${orgId}/projects/${projectId}/knowbase/${knowbaseId}/documents`,
            method: 'POST',
            data: formData,
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            timeout: API_CONFIG.LONG_TIMEOUT
        });
    },

    /**
     * @function updateDocument
     * @description 知識庫上傳
     * @param {string} orgId - 組織 ID
     * @param {string} projectId - 項目 ID
     * @param {string} knowbaseId - 知識庫 ID
     * @param formData
     * @returns {Promise} 代表知識庫數據的 Promise 對象
     */
    updateDocument: async (orgId, projectId, knowbaseId, documentId, formData) => {
        return request({
            url: `/v1/organizations/${orgId}/projects/${projectId}/knowbase/${knowbaseId}/documents/${documentId}`,
            method: 'PUT',
            data: formData,
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            timeout: API_CONFIG.LONG_TIMEOUT
        });
    },

    /**
     * @function deleteDocument
     * @description 刪除知識庫文件
     * @param {string} orgId - 組織 ID
     * @param {string} projectId - 專案 ID
     * @param {string} documentId - 文件 ID
     * @returns {Promise} 刪除文件的 Promise 對象
     */
    deleteDocument: async (orgId, projectId, documentId) => {
        return request({
            url: `/v1/organizations/${orgId}/projects/${projectId}/knowbase/documents/${documentId}`,
            method: HttpMethodEnum.DELETE,
            timeout: API_CONFIG.LONG_TIMEOUT
        });
    },

    /**
     * @function publishKnowledgeBase
     * @description 發布知識庫更新
     * @param {string} orgId - 組織 ID
     * @param {string} projectId - 項目 ID
     * @returns {Promise} 代表知識庫數據的 Promise 對象
     */
    publishKnowledgeBase: async (orgId, projectId) => {
        return request({
            url: `/v1/organizations/${orgId}/projects/${projectId}/knowbase/publish`,
            method: 'PUT',
            timeout: API_CONFIG.LONG_TIMEOUT
        });
    },

    /**
     * @function modifyPassword
     * @description 修改用戶資料
     * @param {Object} data - 修改的用戶資料
     * @returns {Promise} 修改用戶資料請求的 Promise 對象
     */
    modifyPassword(data) {
        return request({
            url: '/v1/auth/password/',
            method: HttpMethodEnum.PUT,
            data
        });
    },

    /**
     * @function getDashboardOverview
     * @description 取得儀表板資訊
     * @returns {Promise} 修改用戶資料請求的 Promise 對象
     * @param orgId
     * @param projectId
     * @param timezone
     */
    getDashboardOverview(orgId, projectId, timezone) {
        return request({
            url: `/v1/organizations/${orgId}/projects/${projectId}/dashboard/overview`,
            method: HttpMethodEnum.GET,
            params: { timezone }
        });
    },

    /**
     * @function getChartModels
     * @description 取得費用資訊
     * @returns {Promise} 修改用戶資料請求的 Promise 對象
     * @param orgId
     * @param projectId
     * @param params
     */
    getChartModels(orgId, projectId, params) {
        return request({
            url: `/v1/organizations/${orgId}/projects/${projectId}/dashboard/chart-models`,
            method: HttpMethodEnum.GET,
            params
        });
    },

    /**
     * @function getChartTraces
     * @description 取得發問數資訊
     * @returns {Promise} 修改用戶資料請求的 Promise 對象
     * @param orgId
     * @param projectId
     * @param params
     */
    getChartTraces(orgId, projectId, params) {
        return request({
            url: `/v1/organizations/${orgId}/projects/${projectId}/dashboard/chart-traces`,
            method: HttpMethodEnum.GET,
            params
        });
    },

    /**
     * @function getTraces
     * @description 獲取所有問答記錄
     * @param {Object} params - 查詢參數
     * @param {string} params.orgId - 組織 ID
     * @param {string} params.projectId - 專案 ID
     * @param {string} params.from_time - 開始時間
     * @param {string} params.to_time - 結束時間
     * @param {number} params.limit - 每頁數量
     * @param {number} params.page - 頁碼
     * @param {string} params.zone - 站點類型 (prod/stage)
     * @returns {Promise} 獲取問答記錄的 Promise 對象
     */
    getTraces({ orgId, projectId, from_time, to_time, limit, page, zone = 'prod' }) {
        return request({
            url: `/v1/organizations/${orgId}/projects/${projectId}/traces`,
            method: HttpMethodEnum.GET,
            params: {
                from_time,
                to_time,
                limit,
                page,
                zone
            }
        });
    },

    /**
     * @function getTraces
     * @description 獲取所有問答記錄
     * @param {Object} params - 查詢參數
     * @param {string} params.orgId - 組織 ID
     * @param {string} params.projectId - 專案 ID
     * @param {string} params.from_time - 開始時間
     * @param {string} params.to_time - 結束時間
     * @param {string} params.zone - 站點類型 (prod/stage)
     * @returns {Promise} 獲取問答記錄的 Promise 對象
     */
    getTracesOverview({ orgId, projectId, zone, from_time, to_time, timezone }) {
        return request({
            url: `/v1/organizations/${orgId}/projects/${projectId}/traces/overview`,
            method: 'GET',
            params: {
                zone,
                from_time,
                to_time,
                timezone
            }
        });
    },

    /**
     * @function getSessionTraces
     * @description 獲取特定 session 的問答記錄
     * @param {Object} params - 查詢參數
     * @param {string} params.orgId - 組織 ID
     * @param {string} params.projectId - 專案 ID
     * @param {string} params.session_id - Session ID
     * @param {string} params.zone - 站點類型 (prod/stage)
     * @returns {Promise} 獲取 session 問答記錄的 Promise 對象
     */
    getSessionTraces({ orgId, projectId, session_id, zone = 'prod'  }) {
        return request({
            url: `/v1/organizations/${orgId}/projects/${projectId}/sessions/${session_id}`,
            method: HttpMethodEnum.GET,
            params: {
                zone
            }
        });
    },

    /**
     * @function exportTraces
     * @description 匯出問答記錄
     * @param {Object} params - 查詢參數
     * @param {string} params.orgId - 組織 ID
     * @param {string} params.projectId - 專案 ID
     * @param {string} params.from_time - 開始時間
     * @param {string} params.to_time - 結束時間
     * @param {string} params.timezone - 時區
     * @param {string} params.zone - 站點類型 (prod/stage)
     * @param {boolean} params.includeCost - 是否包含費用匯出 (預設false)
     * @returns {Promise} 匯出問答記錄的 Promise 對象
     */
    exportTraces({ orgId, projectId, from_time, to_time, timezone, zone = 'prod', includeCost = false }) {
        return request({
            url: `/v1/organizations/${orgId}/projects/${projectId}/traces/export`,
            method: HttpMethodEnum.GET,
            params: {
                from_time,
                to_time,
                timezone,
                zone,
                includeCost
            },
            responseType: 'blob', // 設置響應類型為 blob
        });
    },

    /**
     * @function exportSessionTraces
     * @description 匯出特定 session 的問答記錄
     * @param {Object} params - 查詢參數
     * @param {string} params.orgId - 組織 ID
     * @param {string} params.projectId - 專案 ID
     * @param {string} params.session_id - Session ID
     * @param {string} params.zone - 站點類型 (prod/stage)
     * @returns {Promise} 匯出 session 問答記錄的 Promise 對象
     */
    exportSessionTraces({ orgId, projectId, session_id, timezone, zone = 'prod' }) {
        return request({
            url: `/v1/organizations/${orgId}/projects/${projectId}/sessions/${session_id}/export`,
            method: HttpMethodEnum.GET,
            params: {
                timezone,
                zone
            },
            responseType: 'blob'
        });
    },

    /**
     * @function getChatBotSettings
     * @description 獲取ChatBot的設置
     * @param {string} orgId - 組織ID
     * @param {string} projectId - 專案ID
     * @returns {Promise} 獲取ChatBot設置的Promise對象
     */
    getChatBot(orgId, projectId) {
        return request({
            url: `/v1/organizations/${orgId}/projects/${projectId}/chatbot`,
            method: HttpMethodEnum.GET
        });
    },

    /**
     * 獲取聊天機器人用語設定
     * @param {string} orgId - 組織ID
     * @param {string} projectId - 項目ID
     * @returns {Promise} 獲取設定的Promise對象
     */
    getChatbotPhrases(orgId, projectId) {
        return request({
            url: `/v1/organizations/${orgId}/projects/${projectId}/chatbot_settings/phrases`,
            method: 'GET'
        });
    },

    /**
     * 更新聊天機器人用語設定
     * @param {string} orgId - 組織ID
     * @param {string} projectId - 項目ID
     * @param {Object} data - 更新的數據
     * @returns {Promise} 更新設定的Promise對象
     */
    updateChatbotPhrases(orgId, projectId, data) {
        return request({
            url: `/v1/organizations/${orgId}/projects/${projectId}/chatbot_settings/phrases`,
            method: 'PUT',
            data
        });
    },

    /**
     * 獲取提示問題列表
     * @param {string} orgId - 組織ID
     * @param {string} projectId - 項目ID
     * @returns {Promise} 獲取提示問題的Promise對象
     */
    getStarterPrompts(orgId, projectId) {
        return request({
            url: `/v1/organizations/${orgId}/projects/${projectId}/chatbot_settings/starter_prompts`,
            method: 'GET'
        });
    },

    /**
     * 更新提示問題列表
     * @param {string} orgId - 組織ID
     * @param {string} projectId - 項目ID
     * @param {Object} data - 更新的數據
     * @returns {Promise} 更新提示問題的Promise對象
     */
    updateStarterPrompts(orgId, projectId, data) {
        return request({
            url: `/v1/organizations/${orgId}/projects/${projectId}/chatbot_settings/starter_prompts`,
            method: 'PUT',
            data
        });
    },

    /**
     * 獲取速率限制設定
     * @param {string} orgId - 組織ID
     * @param {string} projectId - 項目ID
     * @returns {Promise} 獲取速率限制設定的Promise對象
     */
    getRateLimit(orgId, projectId) {
        return request({
            url: `/v1/organizations/${orgId}/projects/${projectId}/chatbot_settings/rate_limit`,
            method: 'GET'
        });
    },

    /**
     * 更新速率限制設定
     * @param {string} orgId - 組織ID
     * @param {string} projectId - 項目ID
     * @param {Object} data - 更新的數據
     * @returns {Promise} 更新速率限制設定的Promise對象
     */
    updateRateLimit(orgId, projectId, data) {
        return request({
            url: `/v1/organizations/${orgId}/projects/${projectId}/chatbot_settings/rate_limit`,
            method: 'PUT',
            data
        });
    },

    /**
     * 獲取運作狀態設定
     * @param {string} orgId - 組織ID
     * @param {string} projectId - 項目ID
     * @returns {Promise} 獲取運作狀態設定的Promise對象
     */
    getActivation(orgId, projectId) {
        return request({
            url: `/v1/organizations/${orgId}/projects/${projectId}/chatbot_settings/activation`,
            method: 'GET'
        });
    },

    /**
     * 更新運作狀態設定
     * @param {string} orgId - 組織ID
     * @param {string} projectId - 項目ID
     * @param {Object} data - 更新的數據
     * @returns {Promise} 更新運作狀態設定的Promise對象
     */
    updateActivation(orgId, projectId, data) {
        return request({
            url: `/v1/organizations/${orgId}/projects/${projectId}/chatbot_settings/activation`,
            method: 'PUT',
            data
        });
    },

    /**
     * 獲取知識庫事件列表
     * @param {Object} params - 請求參數
     * @returns {Promise} 知識庫事件列表的 Promise 對象
     */
    getKnowbaseEvents({ orgId, projectId, knowbaseId }) {
        return request({
            url: `/v1/organizations/${orgId}/projects/${projectId}/knowbase/${knowbaseId}/events`,
            method: HttpMethodEnum.GET,
            params: {
                userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone
            }
        });
    },

    /**
     * 新增知識庫事件
     * @param {Object} params - 路徑參數
     * @param {Object} data - 請求數據
     * @returns {Promise} 新增請求的 Promise 對象
     */
    createKnowbaseEvent({ orgId, projectId, knowbaseId }, data) {
        return request({
            url: `/v1/organizations/${orgId}/projects/${projectId}/knowbase/${knowbaseId}/events`,
            method: HttpMethodEnum.POST,
            data,
            timeout: API_CONFIG.LONG_TIMEOUT
        });
    },

    /**
     * 刪除知識庫事件
     * @param {Object} params - 請求參數
     * @returns {Promise} 刪除請求的 Promise 對象
     */
    deleteKnowbaseEvent({ orgId, projectId, knowbaseId, eventId }) {
        return request({
            url: `/v1/organizations/${orgId}/projects/${projectId}/knowbase/${knowbaseId}/events/${eventId}`,
            method: HttpMethodEnum.DELETE
        });
    },

    /**
     * 更新知識庫事件
     * @param {Object} params - 路徑參數
     * @param {Object} data - 請求數據
     * @returns {Promise} 更新請求的 Promise 對象
     */
    updateKnowbaseEvent({ orgId, projectId, knowbaseId, eventId }, data) {
        return request({
            url: `/v1/organizations/${orgId}/projects/${projectId}/knowbase/${knowbaseId}/events/${eventId}`,
            method: HttpMethodEnum.PUT,
            data,
            timeout: API_CONFIG.LONG_TIMEOUT
        });
    }
};

export default API;
